import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { usersApi, type MarathonEnrollmentRow } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { lifecycleErrorText, rowActions, type RowAction } from '@/lib/enrollmentActions';
import { buildAccessText, loginURL } from '@/lib/accessText';

export type RowNotice = { tone: 'ok' | 'error'; text: string };

// Значки — только из вырезанного шрифта (iconSubset.test.ts): отсутствующий
// знак шрифт рисует его именем.
const ICONS: Record<RowAction, string> = {
  profile: 'person',
  copyAccess: 'content_copy',
  deactivate: 'undo',
  activate: 'restore',
  block: 'block',
  unblock: 'lock_open',
  remove: 'delete',
  trash: 'delete',
};

/**
 * Меню «⋯» ученика в списке марафона (users этап 4, образец — docs/refs/edvibe).
 *
 * Какие пункты у строки — решает `rowActions` (lib/enrollmentActions.ts):
 * снять и вернуть запись — admin и manager, блокировать и удалять — только admin.
 * Переноса на другой марафон нет до этапа 8 (решение владельца 11.09.2026).
 *
 * ⚠️ В `components/` проверка noHardcodedText запрещает русский текст даже в
 * строках кода — всё через ключи.
 */
export function StudentRowMenu({
  row,
  marathonId,
  onNotice,
}: {
  row: MarathonEnrollmentRow;
  marathonId: string;
  onNotice: (notice: RowNotice) => void;
}) {
  const { t } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const [open, setOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [reason, setReason] = useState('');
  const name = row.student_name || row.student_email || '—';

  // Статуса учётки в строке записи нет — берём при открытии меню и только
  // админу: пункты учётки менеджеру не показываются, и запрос ему не нужен.
  const account = useQuery({
    queryKey: ['admin', 'user', row.student_id],
    queryFn: () => usersApi.get(row.student_id),
    enabled: open && isAdmin,
  });
  const actions = rowActions(row, role, account.data?.status);

  // ⚠️ Список марафона живёт под ДВУМЯ ключами: вкладка «Ученики» с числом в
  // шапке и вкладка «Кураторы и группы». Перечитать один — вторая покажет
  // снятого ученика активным.
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'marathon-students', marathonId] });
    void qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId] });
    void qc.invalidateQueries({ queryKey: ['admin', 'user', row.student_id] });
  };
  const fail = (e: unknown) => onNotice({ tone: 'error', text: lifecycleErrorText(e, t) });

  const deactivate = useMutation({
    mutationFn: () => usersApi.deactivateEnrollment(row.id),
    onSuccess: (res) => {
      refresh();
      onNotice({
        tone: 'ok',
        text: res.already
          ? t('userLifecycle.doneDeactivateAlready', { name })
          : t('userLifecycle.doneDeactivate', { name }),
      });
    },
    onError: fail,
  });

  const activate = useMutation({
    mutationFn: () => usersApi.activateEnrollment(row.id),
    onSuccess: (res) => {
      refresh();
      const parts = [t('userLifecycle.doneActivate', { name })];
      if (res.group_restored) parts.push(t('userLifecycle.doneActivateGroup'));
      if (res.curator_restored) parts.push(t('userLifecycle.doneActivateCurator'));
      if (res.primary_dropped) parts.push(t('userLifecycle.doneActivatePrimary'));
      if (res.access_expired) parts.push(t('userLifecycle.doneActivateExpired'));
      onNotice({ tone: 'ok', text: parts.join(' ') });
    },
    onError: fail,
  });

  const setBlocked = useMutation({
    mutationFn: (blocked: boolean) => (blocked ? usersApi.block(row.student_id) : usersApi.unblock(row.student_id)),
    onSuccess: (_res, blocked) => {
      refresh();
      onNotice({
        tone: 'ok',
        text: blocked ? t('userLifecycle.doneBlock', { name }) : t('userLifecycle.doneUnblock', { name }),
      });
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => usersApi.remove(row.student_id, reason.trim() || undefined),
    onSuccess: (res) => {
      setRemoveOpen(false);
      setReason('');
      refresh();
      void qc.invalidateQueries({ queryKey: ['admin', 'trash'] });
      onNotice({
        tone: 'ok',
        text: res.already
          ? t('userLifecycle.doneRemoveAlready', { name })
          : t('userLifecycle.doneRemove', { name, count: res.enrollments_deactivated }),
      });
    },
    onError: (e) => {
      setRemoveOpen(false);
      fail(e);
    },
  });

  /**
   * Данные для входа — в буфер, как делала кнопка в строке (`AccessCopyButton`).
   * Пароль запрашиваем по нажатию, а не заранее для всего списка. Пустой ответ —
   * не поломка: ученик мог сменить пароль сам, тогда его не знает никто.
   */
  const copyAccess = async () => {
    const email = row.student_email;
    if (!email) return;
    try {
      const res = await usersApi.getIssuedPassword(row.student_id);
      if (!res.available || !res.password) {
        onNotice({ tone: 'error', text: res.reason || t('accessCopy.noPassword') });
        return;
      }
      await navigator.clipboard.writeText(buildAccessText(t, email, res.password, loginURL()));
      onNotice({ tone: 'ok', text: t('userLifecycle.doneCopyAccess', { name }) });
    } catch {
      onNotice({ tone: 'error', text: t('accessCopy.failed') });
    }
  };

  const run = async (action: RowAction) => {
    setOpen(false);
    switch (action) {
      case 'copyAccess':
        await copyAccess();
        return;
      case 'deactivate':
        if (
          await ask({
            message: t('userLifecycle.confirmDeactivate', { name }),
            confirmLabel: t('userLifecycle.actDeactivate'),
            danger: true,
          })
        ) {
          deactivate.mutate();
        }
        return;
      case 'activate':
        // Возврат ничего не отнимает — без подтверждения.
        activate.mutate();
        return;
      case 'block':
        if (
          await ask({
            message: t('adminStudents.confirmBlock', { name }),
            confirmLabel: t('adminStudents.actionBlock'),
            danger: true,
          })
        ) {
          setBlocked.mutate(true);
        }
        return;
      case 'unblock':
        if (
          await ask({
            message: t('adminStudents.confirmUnblock', { name }),
            confirmLabel: t('adminStudents.actionUnblock'),
          })
        ) {
          setBlocked.mutate(false);
        }
        return;
      case 'remove':
        setRemoveOpen(true);
        return;
      default:
        return;
    }
  };

  const label = (action: RowAction) => {
    switch (action) {
      case 'profile':
        return t('userLifecycle.actProfile');
      case 'copyAccess':
        return t('accessCopy.button');
      case 'deactivate':
        return t('userLifecycle.actDeactivate');
      case 'activate':
        return t('userLifecycle.actActivate');
      case 'block':
        return t('adminStudents.actionBlock');
      case 'unblock':
        return t('adminStudents.actionUnblock');
      case 'remove':
        return t('userLifecycle.actRemove');
      case 'trash':
        return t('userLifecycle.actTrash');
    }
  };

  const itemClass = (danger: boolean) =>
    `flex min-h-11 w-full items-center gap-2.5 rounded-button px-3 py-2 text-left text-sm hover:bg-surface-container-low ${
      danger ? 'text-error' : 'text-on-surface'
    }`;

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        data-row-menu
        aria-label={t('userLifecycle.menu')}
        title={t('userLifecycle.menu')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-11 w-11 items-center justify-center rounded-button text-on-surface-variant hover:bg-surface-container-lowest"
      >
        <span className="material-symbols-outlined">more_horiz</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div role="menu" className="absolute right-0 z-30 mt-1 w-52 rounded-card bg-surface-container-lowest p-1 shadow-ambient">
            {actions.map((action) =>
              action === 'profile' || action === 'trash' ? (
                <Link
                  key={action}
                  role="menuitem"
                  data-action={action}
                  to={action === 'profile' ? `/admin/students/${row.student_id}` : '/admin/trash'}
                  onClick={() => setOpen(false)}
                  className={itemClass(false)}
                >
                  <span className="material-symbols-outlined text-lg">{ICONS[action]}</span>
                  {label(action)}
                </Link>
              ) : (
                <button
                  key={action}
                  type="button"
                  role="menuitem"
                  data-action={action}
                  onClick={() => void run(action)}
                  className={itemClass(action === 'remove' || action === 'block')}
                >
                  <span className="material-symbols-outlined text-lg">{ICONS[action]}</span>
                  {label(action)}
                </button>
              ),
            )}
            {isAdmin && account.isLoading && (
              <p className="px-3 py-2 text-xs text-on-surface-variant">{t('common.loading')}</p>
            )}
          </div>
        </>
      )}

      <Modal open={removeOpen} onClose={() => setRemoveOpen(false)} title={t('userLifecycle.removeTitle')} size="sm">
        <p className="text-sm text-on-surface-variant">{t('userLifecycle.removeText', { name })}</p>
        <label className="mt-4 block text-sm font-medium text-on-surface">
          {t('userLifecycle.removeReason')}
          <textarea
            data-remove-reason
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('userLifecycle.removeReasonPlaceholder')}
            className="mt-1 w-full rounded-button bg-surface-container-low px-3 py-2 text-sm text-on-surface"
          />
        </label>
        <div className="mt-5 flex gap-3">
          <Button variant="secondary" fullWidth onClick={() => setRemoveOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" fullWidth data-remove-confirm loading={remove.isPending} onClick={() => remove.mutate()}>
            {t('userLifecycle.removeConfirm')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
